import * as t from 'io-ts';

import {
  PreferenceStoreIdentifier,
  PreferenceStoreWorkflowSettings,
} from '@transcend-io/privacy-types';

import { PreferenceStorePurpose } from './baseCodecs';

/** The format of error returned from lambda. */
export const LambdaErrorReturn = t.partial({
  /** The error string */
  clientErrorMessage: t.string,
});

/** Override types. */
export type LambdaErrorReturn = t.TypeOf<typeof LambdaErrorReturn>;

/** The shape of the message sent by consentStreamProcessor lambda to the consent workflow daemon. */
export const ConsentPreferencesQueueMessage = t.intersection([
  PreferenceStorePurpose,
  t.type({
    /** identifiers associated with the purpose */
    identifiers: t.array(PreferenceStoreIdentifier),
    /** primary key of the consent preference record */
    primaryKey: t.string,
    /** partition of the user */
    partition: t.string,
  }),
  t.partial({
    /** The workflow settings for the purpose */
    workflowSettings: PreferenceStoreWorkflowSettings,
  }),
]);

/** Override types. */
export type ConsentPreferencesQueueMessage = t.TypeOf<
  typeof ConsentPreferencesQueueMessage
>;

/**
 * The system attributes that are used by consent workflow daemon to process messages in order and ensure exactly once processing.
 */
export const ConsentWorkflowMessageSystemAttributes = t.type({
  /**
   * This is a 128 bit sting which determines the order of processing.
   * It is used to ensure that messages are processed in the order they were sent.
   *
   * - Documentation https://docs.aws.amazon.com/sns/latest/dg/fifo-topic-message-ordering.html
   */
  order: t.string,
  /**
   * This is a string that the consumer should keep track of for at least 5 minutes for Exactly Once Delivery.
   * Due to network issues, a message may be sent over multiple times a classic Distributed System issue.
   *
   * - Currently state: Not used, but recommended to store this on redis/temporary storage for at least 5 minutes.
   * - Documentation https://docs.aws.amazon.com/AWSSimpleQueueService/latest/SQSDeveloperGuide/sqs-fifo-queue-message-identifiers.html
   */
  deduplicationId: t.string,
  /**
   * The number of times the message has been received by a consumer.
   * This is useful to track if the message is being retried due to failures.
   */
  receiveCount: t.number,
  /**
   * The receipt handle is a unique identifier for the message.
   * It is used to delete the message from the queue after processing.
   */
  receiptHandle: t.string,
});

/** Override types. */
export type ConsentWorkflowMessageSystemAttributes = t.TypeOf<
  typeof ConsentWorkflowMessageSystemAttributes
>;

/**
 * The shape of message received by the consent workflow daemon.
 *
 * This includes:
 * - The body includes the message sent by the consentStreamProcessor lambda.
 * - The system attributes of the message, like order, deduplicationId, receiveCount, and receiptHandle.
 */
export const ConsentWorkflowMessage = t.type({
  /** The body of the message */
  body: ConsentPreferencesQueueMessage,
  /** The system attributes of the message. */
  system: ConsentWorkflowMessageSystemAttributes,
});

/** Override types. */
export type ConsentWorkflowMessage = t.TypeOf<typeof ConsentWorkflowMessage>;
